import { Box, Grid, Typography, useMediaQuery } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { firmOnboardingAction } from '../../FirmOnboarding/API/Action';
import customToast, { showApiResponseToast } from '../../../utils/customToast';
import { buildRoleAdditionRequests, executeRoleAdditionRequests } from '../../FirmOnboarding/API/roleAdditionUtils';
import { CustomButton } from '../../../v2/MUI_custom_component';
import { toast } from 'react-toastify';
import { GetMeAction } from "../../Auth/Login/API/Action";
import { refreshTokenAction } from '../../../v2/Onboarding/auth/action';
import { fetchSuppliers, resetSuppliers } from '../../Common/Entity/actions/reduxActions';
import ConfirmBusinessInfoSection from '../../FirmOnboarding/Components/ConfirmBusinessInfoSection';
import ConfirmSelectedRolesSection from '../../FirmOnboarding/Components/ConfirmSelectedRolesSection';
import HumbeeModal from '../../../v2/MUI_custom_component/Modal/HumbeeModal';
import DataLostSvg from '../../Illustrration/components/DataLostSvg';
import AddressDetailModal from '../../FirmOnboarding/Components/AddressDetailModal';

const ConfirmScreen = () => { 
    const isMobile = useMediaQuery('(max-width:600px)');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { selected_firm } = useSelector((state) => ({
      selected_firm: state.dashboardReducer.selected_firm,
    }));
    const manufacturerModal = location.state?.manufacturerModal;
    const { 
      selected_manufacturer_roles,
      gst_flow,
      gst_details,
      pan_details,
      operational_details,
      existingFirm,
      manufacturerRoleFlow
    } = useSelector(state => state.FirmOnboardingReducer || {});
    const selectedFirmId = useSelector(state => state.firmsReducer.selected_firm);
    const [showDetailModal, setShowDetailModal] = useState(!manufacturerRoleFlow && !existingFirm);
    const [loading, setLoading] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const payloadFirmId = manufacturerModal ? selected_firm : selectedFirmId;
    
    const onEdit = () => {
        setShowDetailModal(true);
    }
    const handleDeleteClick = (manufacturer) => {
        if (!manufacturer?.manufacturer_id) return;
        dispatch({ 
            type: 'REMOVE_MANUFACTURER_ROLE', 
            payload: manufacturer.manufacturer_id 
        });
        const remainingRoles = selected_manufacturer_roles.filter(
            role => role.manufacturer_id !== manufacturer.manufacturer_id
        );
        const hasGstRequired = remainingRoles.some(role => role.is_gst_required);
        dispatch({ type: 'SET_SHOW_GST_INPUT', payload: hasGstRequired });
    }
    const validateAddressDetails = () => {
        const addressData = operational_details;
        const errors = {};

        if (!addressData) {
            errors.address_line_1 = 'Address Line 1 is required';
            errors.state_id = 'State is required';
            errors.district_id = 'District is required';
            errors.pincode_id = 'Pincode is required';
            errors.location_id = 'City is required';
            setFieldErrors(errors);
            return { isValid: false, errors };
        }

        if (!addressData.firm_name?.trim()) {
            errors.firm_name = 'Firm Name is required';
        }

        if (!addressData.address_line_1?.trim()) {
            errors.address_line_1 = 'Address Line 1 is required';
        }

        if (!addressData.state_id) {
            errors.state_id = 'State is required';
        }

        if (!addressData.district_id) {
            errors.district_id = 'District is required';
        }
        if (!addressData.pincode_id) {
            errors.pincode_id = 'Pincode is required';
        }
        if (!addressData.location_id) {
            errors.location_id = 'City is required';
        }

        setFieldErrors(errors);

        if (Object.keys(errors).length > 0) {
            return { isValid: false, errors };
        }
        return { isValid: true, errors: {} };
    }
    useEffect(() => {
        validateAddressDetails();
    }, [operational_details]);
    useEffect(() => {
        dispatch({ type: 'RESET_MANUFACTURER_ROLE_FLOW' });
    }, []);
    const handleContinue = async() => {
        const { isValid, errors } = validateAddressDetails();
        if (!isValid) {
            setShowDetailModal(true);
            return;
        }
        if (existingFirm) {
            if (!payloadFirmId) {
                customToast.error('Unable to add manufacturer role. Please select a firm and try again.');
                return;
            }
            const roleAdditionRequests = buildRoleAdditionRequests(selected_manufacturer_roles, {
                companyId: (payloadFirmId && existingFirm) ? payloadFirmId : null,
                gst_details,
                pan_details,
                operational_details,
                existingFirm
            });
            setLoading(true);
            const { success } = await executeRoleAdditionRequests(roleAdditionRequests);
            setLoading(false);
            dispatch(resetSuppliers());
            await dispatch(fetchSuppliers());
            navigate('/my-account/firm-details');
            return;
        }
        const registeredAddress = gst_flow ? gst_details : pan_details;
        const payload = {
            company_name: operational_details?.firm_name,
            company_gstin: gst_details?.gst_number || null,
            pancard_no: operational_details?.pan_number || null,
            registered_address: {
                company_address: {
                    address_line_1: registeredAddress?.address_line_1 || operational_details?.address_line_1,
                    address_line_2: registeredAddress?.address_line_2 || null,
                },
                location_id: registeredAddress?.location_id ||  null ,
                state_id: registeredAddress?.state_id || operational_details?.state_id,
                district_id: registeredAddress?.district_id || operational_details?.district_id,
                pincode: registeredAddress?.pincode_id || null,
            },
            operational_address: {
                company_address: {
                    address_line_1: operational_details?.address_line_1 || null,
                    address_line_2: operational_details?.address_line_2 || null,
                },
                location_id: operational_details?.location_id || null,
                state_id: operational_details?.state_id || null,
                district_id: operational_details?.district_id || null,
                pincode: operational_details?.pincode_id || null,
            },
        };
        setLoading(true);
        const { data, error, message } = await firmOnboardingAction(payload);
        setLoading(false);
        if (!error) {
            if(data && data.response_meta){
                showApiResponseToast(data.response_meta, message, {
                    CustomButton,
                    toast,
                    navigate,
                    path: '/my-account/firm-details',
                    label: "View"
                });
            }
            dispatch(resetSuppliers());
            const { error: tokenError, data: tokenData } = await refreshTokenAction();
            const { error: userError, data: loggedInUser } = await GetMeAction();
            
            if (!userError && loggedInUser) {
            if (tokenData) {
                    localStorage.setItem('token', tokenData);
                    dispatch({ type: 'SET_USER', data: loggedInUser });
                    
                }
            }
            if (!data?.company_id) {
                navigate('/my-account/firm-details');
                customToast.error(message);
                return;
            }

            dispatch({ type: 'SET_SELECTED_FIRM', payload: data.company_id });
            
            
            const roleAdditionRequests = buildRoleAdditionRequests(selected_manufacturer_roles, {
                companyId: data.company_id,
                gst_details,
                pan_details,
                operational_details,
            });
           
            const { success } = await executeRoleAdditionRequests(roleAdditionRequests);
            await dispatch(fetchSuppliers());
            navigate('/my-account/firm-details');
        } else {
            customToast.error(message);
        }
    }
  return (
    <Box sx={{
        p: { xs: 2, md: 4 },
        display: 'flex', 
        flexDirection: 'column', 
        gap: 2 
      }}
    >
      <Box sx={{ 
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
        
      }}>
        <Typography variant='h1'>
          Manage Manufacturer & Role
        </Typography>
        <Box sx={{ 
          display: 'flex',
          gap: 2,
          width: '50%',
          justifyContent: 'flex-end',
          display: {xs: 'none', md:'flex' },
        }}>
            <CustomButton
              label="Cancel"
              variant="text"
              onClick={() => setShowCancelModal(true)}
            />
            <CustomButton
              label="I confirm, let’s proceed!"
              variant="filled"
              disabled={selected_manufacturer_roles.length === 0}
              onClick={handleContinue}
              loading={loading}
            />
        </Box>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ConfirmBusinessInfoSection
              isMobile={isMobile}
              gstFlow={gst_flow}
              gstDetails={gst_details}
              panDetails={pan_details}
              operationalDetails={operational_details}
              fieldErrors={fieldErrors}
              onEdit={onEdit}
              editable={!existingFirm && true}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ConfirmSelectedRolesSection
            selectedManufacturerRoles={selected_manufacturer_roles || []}
            isMobile={isMobile}
            onDelete={handleDeleteClick}
            onEditOrAdd={() => {
              dispatch({ type: 'SET_MANUFACTURER_ROLE_FLOW', payload: true });
              navigate('/my-account/add-manufacturer');
            }}
          />
          <Box sx={{ 
                display: 'flex',
                gap: 2,
                flexDirection: 'column',
                justifyContent: 'flex-end',
                display: {xs: 'flex', md:'none' },
                pt: 2
            }}>
                <CustomButton
                    label="I confirm, let’s proceed!"
                    variant="filled"
                    disabled={selected_manufacturer_roles.length === 0}
                    onClick={handleContinue}
                    loading={loading}
                />
                <CustomButton
                    label="Cancel"
                    variant="text"
                    onClick={() => setShowCancelModal(true)}
                />
            </Box>
        </Grid>
      </Grid>
        <AddressDetailModal
            open={showDetailModal}
            onClose={() => setShowDetailModal(false)}
            initialValidationErrors={fieldErrors}
            firmDetails={{ operational_details, gst_details, pan_details, gst_flow }}
        />
        {console.log('fieldErrors', fieldErrors)}
      <HumbeeModal
        open={showCancelModal}
        onClose={()=> setShowCancelModal(false)}
        onConfirm={() => navigate('/my-account/firm-details')}
        title="Are you sure you want to cancel?"
        subtitle={`All data would be lost.`}
        confirmLabel="Yes"
        cancelLabel="No"
        illustration={<DataLostSvg />}
        width={{ xs: 400 , md:  600 }}
    />
    </Box>
    
  )
}

export default ConfirmScreen
